/**
 * Web Lifecycle Bridge
 *
 * Browser-side counterpart to native-lifecycle-bridge.ts. When the desktop tab
 * becomes visible again or the browser reports the network has come back
 * online, force a fresh WebSocket connection so we recover from sockets the
 * OS may have killed silently (laptop sleep/wake, tab throttling, NAT
 * timeouts). The mobile shell already handles foreground/background via
 * postMessage, so this bridge only attaches when NOT loaded from mobile.
 *
 * Dependencies are injected to avoid a circular dependency with the websocket
 * index and to mirror the shape of native-lifecycle-bridge.ts.
 */
import isLoadedFromMobile from '@app/helpers/platform';

export interface WebLifecycleBridgeDeps {
  reconnectWebSocket: (options?: { force?: boolean }) => Promise<unknown>;
}

let visibilityHandler: (() => void) | null = null;
let onlineHandler: (() => void) | null = null;
let bridgeDeps: WebLifecycleBridgeDeps | null = null;

function triggerForceReconnect(reason: string): void {
  if (!bridgeDeps) return;
  console.log(`[WEBSOCKET_LIFECYCLE] ${reason}, forcing reconnect`);
  bridgeDeps.reconnectWebSocket({ force: true }).catch((error) => {
    console.error(
      `[WEBSOCKET_LIFECYCLE] Failed to reconnect after ${reason}:`,
      error,
    );
  });
}

/**
 * Initialize the web lifecycle bridge. Safe to call on all platforms — the
 * listeners are only registered when the app is NOT loaded inside a mobile
 * WebView (the mobile shell drives reconnection through its own bridge).
 */
export function initWebLifecycleBridge(deps: WebLifecycleBridgeDeps): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (isLoadedFromMobile()) return;
  if (visibilityHandler || onlineHandler) return; // Already initialized

  bridgeDeps = deps;

  visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      triggerForceReconnect('tab visible');
    }
  };

  onlineHandler = () => {
    triggerForceReconnect('network online');
  };

  document.addEventListener('visibilitychange', visibilityHandler);
  window.addEventListener('online', onlineHandler);
  console.log('[WEBSOCKET_LIFECYCLE] Web lifecycle bridge initialized');
}

/**
 * Tear down the web lifecycle bridge. Idempotent.
 */
export function destroyWebLifecycleBridge(): void {
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler);
    onlineHandler = null;
  }
  if (bridgeDeps) {
    bridgeDeps = null;
    console.log('[WEBSOCKET_LIFECYCLE] Web lifecycle bridge destroyed');
  }
}
